import Toolbar from '@material-ui/core/Toolbar';
import clsx from 'clsx';
import IconButton from '@material-ui/core/IconButton';
import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import { createStyles, lighten, makeStyles, Theme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import {
  Add,
  AttachMoney,
  ChevronRight,
  Close,
  Delete,
  Edit,
  EditOutlined,
  ExpandMore,
  Info,
  Person,
  Print,
  Publish,
  Save,
  Search,
  Share,
} from '@material-ui/icons';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import Tooltip from '@material-ui/core/Tooltip';
import { useReactToPrint } from 'react-to-print';
import { useDispatch, useSelector } from 'react-redux';
import { Hidden, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import {
  ProcoreButton,
  SmallButton,
  SubmitButton,
  TooltipIfDisabledComponent,
} from '../custom-components/CustomButtons';
import CSVIcon from '../icons/CSV-icon';
import DocumentLogFilterDialog, {
  DocumentLogFilters,
  setDocumentLogFilters,
} from '../dialogs/DocumentLogFilterDialog';
import EditDocumentsDialog from '../dialogs/EditDocumentsDialog';
import AddPackageDialog from '../dialogs/AddPackageDialog';
import SelectSubmittalsDialog, {
  SubmittalPackageSelection,
} from '../dashboard/SelectSubmittalsDialog';
import {
  ActionTakenType,
  DocumentTemplateType,
  INumberedDocumentView,
  IProjectView,
  SecurityPermissionLevel,
  TaskManagerPermission,
  WorkflowStatusType,
} from '../../api-client/autogenerated';
import AddDocumentDialog from '../dialogs/AddDocumentDialog';
import { addSpacing, getCsvRowsFromDocumentIndexRows, IPackage } from './DocumentIndexUtils';
import type { PunchListLocationRow } from './DocumentIndex';
import { getActionTakenLabelOverrides } from '../../features/action-taken-labels/selectors';
import {
  getDocumentsState,
  getDocumentsType,
  getSelectedDivision,
  getSelectedSection,
} from '../../features/documents/selectors';
import {
  completeReviewForPackageByDocumentId,
  deleteDocumentsByIds,
  modifyDocumentsByBatch,
  restoreDocumentsByIds,
  sendDocumentPublicLinkEmails,
} from '../../models/api/documents';
import AssociateUsersWithDocumentsDialog from '../dialogs/AssociateUsersWithDocumentsDialog';
import { AssociationDocument, AssociationDocumentType } from '../dialogs/associateUsersGrouping';
import { getCurrentSecurityGroup } from '../../features/security/selectors';
import { getUserState } from '../../features/user/selectors';
import { reloadDocuments } from '../../features/documents/actions';
import {
  beginSyncPunchLists,
  beginSyncRfis,
  beginSyncSubmittalsAndPackages,
} from '../../models/api/procore';
import { addSnackbar } from '../../features/snackbar/actions';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCube } from '@fortawesome/free-solid-svg-icons/faCube';
import { divisionToDisplayString } from '../document-display/DocumentDisplay';
import EditResponsiblePartyDialog from '../punch-lists/EditResponsiblePartyDialog';
import {
  docTypesNoCreateOrEdit,
  downloadFileById,
  getUserFriendlyDocumentTemplateNamePlural,
} from '../../scripts/utils';
import EditPunchListStatusDialog from '../punch-lists/EditPunchListStatusDialog';
import ViewByFloorplanDialog from '../punch-lists/ViewByFloorplanDialog';
import PunchListExportDialog from '../punch-lists/PunchListExportDialog';
import PunchListExportSelectionDialog from '../punch-lists/PunchListExportSelectionDialog';
import {
  getDisableProcoreIntegrationFeatures,
  getPhysicalBuildingsState,
  getPhysicalFloorsState,
  getPhysicalLocationsState,
} from '../../features/project/selectors';
import { faCopy } from '@fortawesome/free-regular-svg-icons/faCopy';
import { getDocumentPublicLink } from '../../models/api/users';
import { getDocPermission } from '../../scripts/store-utils';
import EditAsBuiltStatusDialog from '../dialogs/EditAsBuiltStatusDialog';
import EmailUsersDialog from '../design/EmailUsersDialog';
import { emitter, Events } from '../../scripts/events';
import CircularLoader from '../loader/CircularLoader';
import RefusePackageDialog from '../submittal-action-center/packages/RefusePackageDialog';
import { SubmittalInfo } from '../submittal-action-center/packages/PackageListTable';
import CreatePunchListDialog from '../punch-lists/CreatePunchListDialog';
import CsvDownloader from 'react-csv-downloader';
import EditDrawingsPackageDialog from '../dialogs/EditDrawingsPackageDialog';
import { getDocumentsLoadingState } from '../../features/loading/selectors';
import { useAppUtilsContext } from '../AppUtilsContext';
import { ARCHIVED_PROJECT_TOOLTIP, isProjectArchived } from '../../scripts/permission-utils';

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      padding: '12px 0px',
      [theme.breakpoints.down('xs')]: {
        padding: '0px 0px 16px',
      },
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      rowGap: '12px',
      // The full width at every size, so Filter, Search and the export buttons end at the table's
      // right edge.
      width: '100%',
      marginTop: '8px',
    },
    /** One line of the toolbar; wraps when the window is too narrow for it. */
    row: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      columnGap: 8,
      rowGap: '8px',
      width: '100%',
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        alignItems: 'baseline',
      },
    },
    /** The first row: the title and buttons start at its top. */
    actionRow: {
      alignItems: 'flex-start',
    },
    /**
     * Print, CSV and Export: kept at the foot of the first row, however many lines its buttons wrap
     * to, so they stay just above Filter and Search.
     */
    actionRowEnd: {
      alignSelf: 'flex-end',
      // Exactly as tall as the action buttons, with the icons centred in it, so they line up with
      // the last line of buttons. The CSV icon's button stands a few pixels taller and overhangs the
      // band evenly above and below, into the gap between the rows.
      height: 32,
      '& > div': {
        display: 'flex',
      },
    },
    /**
     * The second row. When it is too narrow for one line, Filter and Search go up a line and Expand
     * All stays at the bottom, directly above the table.
     */
    filterRow: {
      [theme.breakpoints.up('sm')]: {
        flexWrap: 'wrap-reverse',
      },
    },
    /**
     * On a phone, where the second row stacks, Expand All goes below Filter and Search too, so they
     * stay right under Print, CSV and Export and it stays directly above the table.
     */
    expandAll: {
      [theme.breakpoints.down('xs')]: {
        order: 1,
      },
    },
    /**
     * The action buttons. Sized from nothing and grown into the free space, so they wrap within the
     * room the right-hand end leaves them instead of pushing it onto a line of its own -- but never
     * narrower than their widest button, which would be cut off. Short of that room, the right-hand
     * end drops to a line of its own instead, still just above Filter and Search.
     */
    actionButtons: {
      display: 'flex',
      flexWrap: 'wrap',
      rowGap: '12px',
      columnGap: 8,
      whiteSpace: 'nowrap',
      flex: '1 1 0',
      minWidth: 'min-content',
      [theme.breakpoints.down('xs')]: {
        flex: 'none',
      },
    },
    /**
     * The right-hand end of a row. One flex item, so what it holds stays together and right-aligned
     * however the rest of the row wraps.
     */
    rowEnd: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      columnGap: 8,
      marginLeft: 'auto',
      whiteSpace: 'nowrap',
    },
    highlight:
      theme.palette.type === 'light'
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
    title: {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '26px',
      lineHeight: '30px',
      color: '#464546',
      textTransform: 'none',
      marginRight: 24,
    },
    button: {
      paddingLeft: '16px',
      paddingRight: '16px',
      // marginRight: '12px',
      [theme.breakpoints.down('xs')]: {
        padding: '6px 16px',
        // margin: '5px 0px',
      },
    },
    filterButton: {
      [theme.breakpoints.down('xs')]: {
        lineHeight: '32px',
      },
    },
    icon: {
      fill: '#B2B1B2',
    },
    formControl: {
      width: '216px',
      [theme.breakpoints.down('xs')]: {
        flexGrow: 1,
      },
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        display: 'flex',
      },
    },
    packageIcon: {
      marginLeft: 8,
    },
    deleteButton: {
      backgroundColor: '#ED3F26',
      '&:hover': {
        backgroundColor: '#FF5D45',
      },
      '&:focus': {
        backgroundColor: '#FF5D45',
      },
    },
  }),
);

export interface PunchListCsvRow {
  building: string;
  floor: string;
  location: string;
  description: string;
  subcontractor: string;
  status: string;
  cost: string;
  postedDate: string;
}

const punchListCsvHeaders = [
  { displayName: 'Building', id: 'building' },
  { displayName: 'Floor', id: 'floor' },
  { displayName: 'Room', id: 'location' },
  { displayName: 'Description', id: 'description' },
  { displayName: 'Responsible Subcontractor', id: 'subcontractor' },
  { displayName: 'Status', id: 'status' },
  { displayName: 'Cost', id: 'cost' },
  { displayName: 'Posted', id: 'postedDate' },
  { displayName: 'Public Document Link', id: 'publicDocumentLink' },
];

interface DocumentIndexToolbarProps {
  selected: string[];
  selectedProject: IProjectView;
  updateSearch: (input: string) => void;
  filteredRows: any[];
  packages: IPackage[];
  filters: DocumentLogFilters;
  setFilters: setDocumentLogFilters;
  updateRows: () => void;
  addToCloseout: () => void;
  removeFromCloseout: () => void;
  selectedAreProcoreSynced?: boolean;
  componentReference: any;
  selectedDocument: INumberedDocumentView | undefined;
  canChangeAnticipatedSubmission?: boolean;
  uploadWorkflowOpen?: boolean;
  setUploadWorkflowOpen?: React.Dispatch<SetStateAction<boolean>>;
  disableCreatePackage?: boolean;
  isEditingCost: boolean;
  setIsEditingCost: React.Dispatch<SetStateAction<boolean>>;
  submitCosts: () => Promise<void>;
  visible?: boolean;
  setVisible?: Function;
  setIndexDrawerOpen?: (input: boolean) => void;
  taskScope?: 'me' | 'company' | 'everyone';
  setTaskScope?: (scope: 'me' | 'company' | 'everyone') => void;
  taskManagerPermission?: TaskManagerPermission | null;
}

const docTypesNoFilter = [
  DocumentTemplateType.BidDrawings,
  DocumentTemplateType.ProjectManual,
  DocumentTemplateType.Addenda,
  DocumentTemplateType.InformationalItems,
  DocumentTemplateType.BidTabulation,
  DocumentTemplateType.RegulatoryApprovals,
  DocumentTemplateType.AsiDocuments,
  DocumentTemplateType.ChangeOrders,
  DocumentTemplateType.ConstructionChangeDirectives,
  DocumentTemplateType.MiscellaneousDocuments,
  DocumentTemplateType.Schedules,
  DocumentTemplateType.Testing,
  DocumentTemplateType.CertificateOfSubstantialCompletion,
  DocumentTemplateType.ClearLp,
  // TODO: Remove below line when "Source Document" filter is re-enabled
  DocumentTemplateType.Specifications,
];

const docTypesProcoreFilter = [
  DocumentTemplateType.ContractorDailyLogs,
  DocumentTemplateType.MeetingMinutes,
];

const docTypesUploadWorkflow = [
  DocumentTemplateType.Submittals,
  DocumentTemplateType.CloseoutSubmittals,
  DocumentTemplateType.AsBuilt,
];

export default function DocumentIndexToolbar(props: DocumentIndexToolbarProps) {
  const classes = useToolbarStyles();
  const {
    selected,
    selectedProject,
    updateSearch,
    filteredRows,
    packages,
    filters,
    setFilters,
    updateRows,
    addToCloseout,
    removeFromCloseout,
    selectedAreProcoreSynced = false,
    componentReference,
    selectedDocument,
    canChangeAnticipatedSubmission = false,
    uploadWorkflowOpen,
    setUploadWorkflowOpen,
    disableCreatePackage,
    isEditingCost,
    setIsEditingCost,
    submitCosts,
    visible,
    setIndexDrawerOpen,
    taskScope,
    setTaskScope,
    taskManagerPermission,
  } = props;

  const history = useHistory<{ openCreateDialog?: boolean } | undefined>();
  const dispatch = useDispatch();
  const user = useSelector(getUserState);
  const actionTakenLabels = useSelector(getActionTakenLabelOverrides);
  const documents = useSelector(getDocumentsState);
  const documentsLoading = useSelector(getDocumentsLoadingState);
  const selectedDivision = useSelector(getSelectedDivision);
  const selectedSection = useSelector(getSelectedSection);
  const documentType = useSelector(getDocumentsType);
  const disableProcoreIntegrationFeatures = useSelector(getDisableProcoreIntegrationFeatures);
  const security = useSelector(getCurrentSecurityGroup);
  const locations = useSelector(getPhysicalLocationsState);
  const floors = useSelector(getPhysicalFloorsState);
  const buildings = useSelector(getPhysicalBuildingsState);
  const permission = getDocPermission() || SecurityPermissionLevel.NUMBER_0;
  const isArchivedProject = isProjectArchived(selectedProject);

  const {
    documentTemplates: { getTemplateId },
  } = useAppUtilsContext();

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addEditPermission, setAddEditPermission] = useState(false);
  const [createPackagePermission, setCreatePackagePermission] = useState(false);
  const [createPlaceholderPackagePermission, setCreatePlaceholderPackagePermission] = useState(
    false,
  );
  const [deletePermission, setDeletePermission] = useState(false);

  const [editDrawingsPackageDialogOpen, setEditDrawingsPackageDialogOpen] = useState(false);
  const [addPackageDialogOpen, setAddPackageDialogOpen] = useState(false);
  const [addPlaceholderPackageDialogOpen, setAddPlaceholderPackageDialogOpen] = useState(false);
  // The dashboard's two-step package flow, offered here too: the picker gathers the submittals,
  // then AddPackageDialog names the package. Held separately from the selection-driven dialogs
  // above so neither path disturbs the other.
  const [selectSubmittalsOpen, setSelectSubmittalsOpen] = useState(false);
  const [packageSelections, setPackageSelections] = useState<SubmittalPackageSelection[]>();
  const [associateDialogOpen, setAssociateDialogOpen] = useState(false);
  const [inputSearch, setInputSearch] = useState<string>();

  const editDrawingsPackageAnchor = useRef<HTMLButtonElement | null>(null);
  const filterAnchor = useRef<any>(null);
  const editAnchor = useRef<any>(null);
  const associateAnchor = useRef<any>(null);
  const packageAnchor = useRef<any>(null);
  const placeholderPackageAnchor = useRef<any>(null);
  const addAnchor = useRef<any>(null);
  const responsiblePartyAnchor = useRef<HTMLButtonElement>(null);
  const punchListStatusAnchor = useRef<HTMLButtonElement>(null);
  const editCostAnchor = useRef<HTMLButtonElement>(null);
  const sendALinkAnchor = useRef<HTMLButtonElement>(null);

  const [numFilters, setNumFilters] = useState(0);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
  const [isSyncingWithProcore, setIsSyncingWithProcore] = useState(false);
  const [editResponsiblePartyDialogOpen, setEditResponsiblePartyDialogOpen] = useState(false);
  const [editPunchListStatusDialogOpen, setEditPunchListStatusDialogOpen] = useState(false);
  const [sendALinkDialogOpen, setSendALinkDialogOpen] = useState(false);
  const [punchListExportSelectionOpen, setPunchListExportSelectionOpen] = useState(false);
  /** The items of the export under way; set once the selection dialog is submitted. */
  const [punchListExportDocumentIds, setPunchListExportDocumentIds] = useState<string[]>();

  const [editAsBuiltStatusDialogOpen, setEditAsBuiltStatusDialogOpen] = useState(false);
  const [createPunchListDialogOpen, setCreatePunchListDialogOpen] = useState(false);

  const [floorplanDialogOpen, setFloorplanDialogOpen] = useState(false);

  // ids of Punch List location rows that are currently expanded, kept in sync by
  // DocumentIndexRow's Events.RowExpandStateChanged emits, for the Expand All/Collapse
  // All toggle button below
  const [expandedLocationIds, setExpandedLocationIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleRowExpandStateChanged = (id: string, expanded: boolean) => {
      setExpandedLocationIds((prev) => {
        const next = new Set(prev);
        if (expanded) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
    };

    emitter.on(Events.RowExpandStateChanged, handleRowExpandStateChanged);

    return () => {
      emitter.removeListener(Events.RowExpandStateChanged, handleRowExpandStateChanged);
    };
  }, []);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishingPunchListItems, setIsPublishingPunchListItems] = useState(false);

  const [csvLoading, setCsvLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery('(min-width:600px)');

  const selectedDocuments = selected
    .map((documentId) => documents.find((doc) => doc.id === documentId))
    .filter((x) => !!x) as INumberedDocumentView[];

  const disableCreatePunchListItems =
    documentType === DocumentTemplateType.PunchList && locations.length === 0;

  // whether every currently-visible Punch List location row is expanded, used to decide
  // between showing "Expand All" and "Collapse All"; intersecting with the currently
  // filtered rows (rather than trusting expandedLocationIds alone) keeps this correct
  // when filtering unmounts a row, which resets it back to collapsed on remount
  const allLocationsExpanded =
    documentType === DocumentTemplateType.PunchList &&
    (filteredRows as PunchListLocationRow[]).length > 0 &&
    (filteredRows as PunchListLocationRow[]).every((row) =>
      expandedLocationIds.has(row.location.id),
    );

  const isAsBuiltPackage =
    documentType === DocumentTemplateType.AsBuilt &&
    !!selectedDocument?.submittalChildDocuments?.length;

  const hasSelectedDeletedDocuments = selectedDocuments.some((d) => !!d.isHidden);

  // Drawings/Specifications rows currently on the page, for the Associate Users dialog.
  const associableDocuments: AssociationDocument[] =
    documentType === DocumentTemplateType.Drawings ||
    documentType === DocumentTemplateType.Specifications
      ? filteredRows
          .filter((row) => !row.isHidden)
          .map((row) => ({ id: row.id, number: row.number || '', title: row.title }))
      : [];

  const hasSelectedWithdrawnDocuments = selectedDocuments.some(
    (d) => d.actionTaken === ActionTakenType.Withdrawn,
  );

  // Every write this toolbar offers -- Create, Edit, Delete, Create Package, Create Placeholder
  // Package -- hangs off these four flags, so an archived project clears all four here rather
  // than each button checking for itself. Ahead of the admin branch below: the API refuses an
  // admin's writes to an archived project too.
  useEffect(() => {
    if (isArchivedProject) {
      setAddEditPermission(false);
      setCreatePackagePermission(false);
      setCreatePlaceholderPackagePermission(false);
      setDeletePermission(false);
    } else if (!user.isSiteAdmin && user.adminOfSubscriberId !== selectedProject?.subscriberId) {
      if (documentType) {
        const documentTemplateId = getTemplateId(documentType);
        const permissionLevel = security?.securityGroupDocumentTemplateList?.find(
          (group) => group.documentTemplateId === documentTemplateId,
        )?.securityPermissionLevel;
        if (permissionLevel && permissionLevel >= 2) {
          setAddEditPermission(true);
          setCreatePackagePermission(true);
          if (permissionLevel >= 3) {
            setCreatePlaceholderPackagePermission(true);
          } else {
            setCreatePlaceholderPackagePermission(false);
          }
        } else {
          const additionalReviewDocumentTemplateId = getTemplateId(
            DocumentTemplateType.AdditionalReview,
          );

          const permissionLevel = security?.securityGroupDocumentTemplateList?.find(
            (group) => group.documentTemplateId === additionalReviewDocumentTemplateId,
          )?.securityPermissionLevel;
          if (permissionLevel && permissionLevel === 3) setCreatePackagePermission(true);

          setAddEditPermission(false);
        }

        if (
          documentType === DocumentTemplateType.Task ||
          documentType === DocumentTemplateType.CaTask
        ) {
          // Task / CA Task deletion requires Admin-level task-manager permission, not the ALL
          // shadow (which every review-capable group carries).
          const taskManagerPermission = security?.securityGroupDocumentTemplateList?.find(
            (group) => group.documentTemplateId === documentTemplateId,
          )?.taskManagerPermission;
          if (taskManagerPermission === TaskManagerPermission.NUMBER_4) {
            setDeletePermission(true);
          }
        } else if (permissionLevel && permissionLevel === 4) {
          setDeletePermission(true);
        }
      }
    } else {
      setAddEditPermission(true);
      setCreatePackagePermission(true);
      setCreatePlaceholderPackagePermission(true);
      setDeletePermission(true);
    }
  }, [documentType, security, user, selectedProject, isArchivedProject]);

  useEffect(() => {
    if (history.location.state?.openCreateDialog) {
      setAddDialogOpen(true);
      history.location.state = undefined;
    }
  }, [history]);

  // This toolbar instance is reused (not remounted) when the user switches to a different
  // document table (e.g. a different tab/document type or project), since it isn't keyed on
  // those values. Reset the delete-button loading state whenever the table scope changes so a
  // delete left in flight (or abandoned) on the previous table doesn't leave the new table's
  // Delete button stuck showing a spinner.
  useEffect(() => {
    setIsDeleting(false);
  }, [documentType, selectedProject?.id]);

  const getDrawerText = () => {
    if (!documentType) return 'Open Drawer';
    return `Open ${
      documentType === DocumentTemplateType.Submittals ||
      documentType === DocumentTemplateType.CloseoutSubmittals
        ? 'Submittals'
        : 'Specifications'
    } Index`;
  };

  const getFilterText = () => {
    if (numFilters === 0)
      return (
        <>
          <Add style={{ paddingRight: isDesktop ? 2 : 0 }} />
          FILTER
        </>
      );
    if (numFilters === 1) return '1 FILTER SELECTED';
    return numFilters.toString().concat(' FILTERS SELECTED');
  };

  const sendSearchUpdate = (newValue: string) => {
    setSearchTimeout(undefined);
    updateSearch(newValue || '');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value;
    setInputSearch(newValue);
    if (!searchTimeout) {
      setSearchTimeout(setTimeout(() => sendSearchUpdate(newValue), 600));
    } else {
      clearTimeout(searchTimeout);
      setSearchTimeout(setTimeout(() => sendSearchUpdate(newValue), 600));
    }
  };

  const getTitle = () => {
    if (
      documentType === DocumentTemplateType.Submittals ||
      documentType === DocumentTemplateType.CloseoutSubmittals
    ) {
      if (selectedSection) {
        return `${addSpacing(selectedSection.number)} ${selectedSection.title || ''}`;
      }
      if (selectedDivision) {
        return `Division ${selectedDivision} - ${
          divisionToDisplayString[selectedDivision] || 'Unknown'
        }`;
      }
      return 'All Submittals';
    }
    return '';
  };

  const getCreateDisabled = () => {
    if (!addEditPermission) return true;

    if (
      disableProcoreIntegrationFeatures &&
      (documentType === DocumentTemplateType.Submittals ||
        documentType === DocumentTemplateType.RequestsForInformation)
    )
      return true;

    if (disableCreatePunchListItems) return true;

    if (
      documentType === DocumentTemplateType.CertificateOfSubstantialCompletion &&
      (documents?.filter((d) => !d.isHidden && d.actionTaken !== ActionTakenType.Withdrawn).length >
        0 ||
        documentsLoading)
    ) {
      return true;
    }

    return false;
  };

  const getDisabledTooltipTitle = () => {
    if (isArchivedProject) return ARCHIVED_PROJECT_TOOLTIP;
    if (
      disableProcoreIntegrationFeatures &&
      (documentType === DocumentTemplateType.Submittals ||
        documentType === DocumentTemplateType.RequestsForInformation)
    )
      return 'This is disabled for projects synced with Procore';
    if (disableCreatePunchListItems)
      return 'Complete the setup process before Punch List items can created.';
    return '';
  };

  const getCreatePackageDisabled = () => {
    if (isArchivedProject) return true;
    if (hasSelectedDeletedDocuments || hasSelectedWithdrawnDocuments) return true;
    if (
      selected.length <= 0 ||
      (disableProcoreIntegrationFeatures &&
        (documentType === DocumentTemplateType.Submittals ||
          documentType === DocumentTemplateType.CloseoutSubmittals))
    ) {
      return true;
    }
    if (selected.length > 1 && disableCreatePackage) {
      return true;
    }
    if (
      documentType === DocumentTemplateType.AsBuilt &&
      selected.length === 1 &&
      selectedDocument
    ) {
      return (
        selectedDocument.workflowStatus !== WorkflowStatusType.Initial ||
        !!selectedDocument.submittalPackageDocumentId
      );
    }
    return selected.length > 1 && !createPackagePermission;
  };

  const getCreatePlaceholderPackageDisabled = () => {
    if (hasSelectedWithdrawnDocuments || hasSelectedDeletedDocuments) return true;
    if (
      selected.length <= 0 ||
      disableCreatePackage ||
      (disableProcoreIntegrationFeatures && documentType === DocumentTemplateType.Submittals)
    )
      return true;

    if (
      documentType === DocumentTemplateType.AsBuilt &&
      selected.length === 1 &&
      selectedDocument
    ) {
      return selectedDocument.workflowStatus !== WorkflowStatusType.Initial;
    }

    return !createPlaceholderPackagePermission || selected.length < 2;
  };

  /**
   * The submittal logs are where the dashboard's package flow also lives, so the same two entry
   * points are offered here, opening the same two dialogs: a reviewer creates the placeholder,
   * anyone with only create permission uploads the package itself. Which one is the split
   * DocumentIndexToolbar already draws between createPlaceholderPackagePermission and
   * createPackagePermission.
   *
   * The picker gathers its own submittals, so the button ignores the log's ticked rows entirely
   * and is live as soon as the project has a submittal to gather. The rest of what can refuse a
   * package -- an archived project, too few submittals -- the picker's own action button refuses,
   * exactly as it does on the dashboard.
   */
  const isSubmittalPackageLog =
    documentType === DocumentTemplateType.Submittals ||
    documentType === DocumentTemplateType.CloseoutSubmittals;
  const packagePickerIsPlaceholder = createPlaceholderPackagePermission;
  const canOpenSubmittalPackagePicker =
    isSubmittalPackageLog && (createPlaceholderPackagePermission || createPackagePermission);

  /** What this log calls the documents it holds, for the button's own wording. */
  const packagePickerTypeWord =
    documentType === DocumentTemplateType.CloseoutSubmittals ? 'Closeout Submittal' : 'Submittal';

  /**
   * Procore owns the submittals of a synced project, so a package cannot be built here at all --
   * unlike the conditions the picker itself weighs, this one is settled before it opens.
   */
  const getPackagePickerBlockedReason = () => {
    if (disableProcoreIntegrationFeatures) return 'Currently Syncing with Procore';
    if (documents.length === 0)
      return `This project has no ${packagePickerTypeWord.toLowerCase()}s to gather into a package yet.`;
    return undefined;
  };

  const getEditAsBuiltStatusDisabled = () => {
    return (
      isArchivedProject ||
      hasSelectedWithdrawnDocuments ||
      hasSelectedDeletedDocuments ||
      selected.length !== 1 ||
      permission < SecurityPermissionLevel.NUMBER_3 ||
      !selectedDocument?.workflowStatus ||
      ![WorkflowStatusType.SubmittedForReview, WorkflowStatusType.UnderReview].includes(
        selectedDocument.workflowStatus,
      ) ||
      !!selectedDocument.submittalPackageDocumentId
    );
  };

  const handleSendEmails = async (emails: string[]) => {
    await Promise.all(
      selected.map((documentId) => sendDocumentPublicLinkEmails(documentId, emails)),
    );
  };

  const handleGetLink = async () => {
    if (selected.length !== 1) return '';
    return getDocumentPublicLink(selected[0]);
  };

  const handleDownloadFiles = async () => {
    setIsDownloading(true);
    try {
      const rowsToDownload =
        selected.length > 0 ? filteredRows.filter((r) => selected.includes(r.id)) : filteredRows;
      for (let i = 0; i < rowsToDownload.length; i += 1) {
        const row = rowsToDownload[i];
        const filename =
          documentType === DocumentTemplateType.Drawings
            ? `${row.number}${row.title && row.title !== 'N/A' ? `_${row.title}` : ''}.pdf`
            : row.viewButton.name;
        await downloadFileById(row.viewButton.id, filename);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDeleteDocuments = async () => {
    const shouldDelete = window.confirm(
      `Are you sure you want to delete ${
        selected.length === 1 ? 'this document' : 'these documents'
      }?`,
    );
    if (shouldDelete)
      try {
        setIsDeleting(true);
        await deleteDocumentsByIds(selected);
        dispatch(reloadDocuments());
      } catch (e: any) {
        console.error(e);
      } finally {
        setIsDeleting(false);
      }
  };

  const handlePublishPunchListItems = async () => {
    setIsPublishingPunchListItems(true);
    try {
      const selectedDraftDocumentIds = selectedDocuments.filter((d) => d.isDraft).map((d) => d.id);
      await modifyDocumentsByBatch(selectedDraftDocumentIds, { patch: { isDraft: false } });
      dispatch(reloadDocuments());
    } finally {
      setIsPublishingPunchListItems(false);
    }
  };

  const getToolbarButton = () => {
    const buttons: (JSX.Element | null)[] = [];
    const shouldCreatePlaceholder = permission && permission >= SecurityPermissionLevel.NUMBER_3;

    if (documentType === DocumentTemplateType.AsBuilt) {
      if (uploadWorkflowOpen) {
        buttons.push(
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Close />}
            onClick={() => {
              if (setUploadWorkflowOpen) setUploadWorkflowOpen(false);
            }}
            style={{ height: 32, paddingLeft: 24, paddingRight: 24 }}
          >
            Cancel
          </Button>,
        );
      } else {
        // Both of these start a write -- one uploads As-Builts, the other creates placeholders --
        // and this branch is the one place in the toolbar that never took a disabled flag.
        buttons.push(
          <TooltipIfDisabledComponent
            disabled={isArchivedProject}
            title={ARCHIVED_PROJECT_TOOLTIP}
            // The wrapper's default is a full-width centred span, which would break this button
            // out of the toolbar's row.
            spanStyle={{ width: 'auto' }}
          >
            {!shouldCreatePlaceholder ? (
              <Button
                variant="contained"
                color="secondary"
                disabled={isArchivedProject}
                startIcon={<Add />}
                onClick={() => {
                  if (setUploadWorkflowOpen) setUploadWorkflowOpen(true);
                }}
                style={{ height: 32, paddingLeft: 24, paddingRight: 24 }}
              >
                Upload As-Builts
              </Button>
            ) : (
              <Button
                ref={addAnchor}
                variant="contained"
                color="secondary"
                disabled={isArchivedProject}
                startIcon={<Add />}
                onClick={() => setAddDialogOpen(true)}
                style={{ height: 32, paddingLeft: 24, paddingRight: 24 }}
              >
                Create Placeholder
              </Button>
            )}
          </TooltipIfDisabledComponent>,
        );
      }
    }

    if (docTypesUploadWorkflow.includes(documentType as DocumentTemplateType)) {
      if (uploadWorkflowOpen) {
        buttons.push(
          <TooltipIfDisabledComponent
            title={getDisabledTooltipTitle()}
            disabled={disableProcoreIntegrationFeatures}
          >
            <Button
              ref={packageAnchor}
              variant="contained"
              color="primary"
              disabled={getCreatePackageDisabled()}
              onClick={() => {
                if (selected.length === 1) {
                  if (
                    documentType === DocumentTemplateType.Submittals ||
                    documentType === DocumentTemplateType.CloseoutSubmittals
                  ) {
                    history.push(
                      `${
                        documentType === DocumentTemplateType.Submittals
                          ? 'submittals'
                          : 'closeout-submittals'
                      }/${selected[0]}`,
                    );
                  } else setEditDialogOpen(true);
                } else {
                  setAddPackageDialogOpen(true);
                }
              }}
            >
              {selected.length > 1 ? (
                <>
                  <FontAwesomeIcon icon={faCube} style={{ marginRight: '6px' }} />
                  Create Package
                </>
              ) : (
                'Continue'
              )}
            </Button>
          </TooltipIfDisabledComponent>,
        );
      } else {
        buttons.push(
          documentType === DocumentTemplateType.AsBuilt ? (
            <Button
              variant="contained"
              color="primary"
              disabled={getEditAsBuiltStatusDisabled()}
              ref={editAnchor}
              onClick={() => setEditAsBuiltStatusDialogOpen(true)}
              startIcon={<EditOutlined />}
            >
              Review As-Built
            </Button>
          ) : (
            <SmallButton
              variant="contained"
              color="primary"
              disabled={
                !addEditPermission ||
                selected.length === 0 ||
                hasSelectedWithdrawnDocuments ||
                hasSelectedDeletedDocuments
              }
              ref={editAnchor}
              onClick={() => setEditDialogOpen(true)}
            >
              <EditOutlined style={{ width: 20, height: 20, paddingRight: 2 }} />
              Edit
            </SmallButton>
          ),
        );
      }

      if (canOpenSubmittalPackagePicker) {
        const blockedReason = getPackagePickerBlockedReason();
        buttons.push(
          <TooltipIfDisabledComponent
            disabled={!!blockedReason}
            title={blockedReason ?? ''}
            spanStyle={{ width: 'auto' }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={!!blockedReason}
              ref={placeholderPackageAnchor}
              startIcon={<FontAwesomeIcon icon={faCube} style={{ marginRight: '6px' }} />}
              onClick={() => setSelectSubmittalsOpen(true)}
            >
              {packagePickerIsPlaceholder
                ? `Create ${packagePickerTypeWord} Package Placeholder`
                : `Upload ${packagePickerTypeWord} Package`}
            </Button>
          </TooltipIfDisabledComponent>,
        );
      } else if (createPlaceholderPackagePermission) {
        buttons.push(
          <Button
            variant="contained"
            color="primary"
            disabled={getCreatePlaceholderPackageDisabled()}
            ref={placeholderPackageAnchor}
            startIcon={<FontAwesomeIcon icon={faCube} style={{ marginRight: '6px' }} />}
            onClick={() => setAddPlaceholderPackageDialogOpen(true)}
          >
            Create Placeholder Package
          </Button>,
        );
      }

      if (deletePermission) {
        buttons.push(
          !isDeleting ? (
            <SmallButton
              variant="contained"
              color="primary"
              disabled={selected.length === 0 || hasSelectedDeletedDocuments}
              className={classes.deleteButton}
              style={isDesktop ? { paddingLeft: 12, paddingRight: 12 } : {}}
              onClick={handleDeleteDocuments}
            >
              <Delete style={{ width: 20, height: 20, paddingRight: 2 }} />
              Delete
            </SmallButton>
          ) : (
            <CircularLoader size={30} style={{ marginLeft: 32, marginRight: 32 }} />
          ),
        );
      }
    } else if (!docTypesNoCreateOrEdit.includes(documentType as DocumentTemplateType)) {
      buttons.push(
        selected.length > 0 ? (
          <>
            {documentType !== DocumentTemplateType.PunchList && (
              <SmallButton
                variant="contained"
                color="primary"
                disabled={
                  !addEditPermission ||
                  (documentType === DocumentTemplateType.FieldReports && selected.length !== 1) ||
                  hasSelectedWithdrawnDocuments ||
                  hasSelectedDeletedDocuments ||
                  (documentType === DocumentTemplateType.PotentialChangeOrders &&
                    permission < SecurityPermissionLevel.NUMBER_3)
                }
                ref={editAnchor}
                onClick={() =>
                  documentType !== DocumentTemplateType.FieldReports ||
                  !selectedDocument?.dateOfObservation
                    ? setEditDialogOpen(true)
                    : history.push(`field-reports/${selected[0]}/edit`)
                }
              >
                <EditOutlined style={{ width: 20, height: 20, paddingRight: 2 }} />
                Edit
              </SmallButton>
            )}
          </>
        ) : (
          <span style={{ height: 32 }}>
            <Hidden xsDown>
              <TooltipIfDisabledComponent
                disabled={
                  isArchivedProject ||
                  disableProcoreIntegrationFeatures ||
                  disableCreatePunchListItems
                }
                title={getDisabledTooltipTitle()}
              >
                <Button
                  variant="contained"
                  color="primary"
                  ref={addAnchor}
                  disabled={getCreateDisabled()}
                  className={classes.button}
                  onClick={() => {
                    if (documentType === DocumentTemplateType.PunchList) {
                      setCreatePunchListDialogOpen(true);
                    } else {
                      setAddDialogOpen(true);
                    }
                  }}
                >
                  <Add style={{ paddingRight: 2 }} />
                  CREATE
                </Button>
              </TooltipIfDisabledComponent>
            </Hidden>
            <Hidden smUp>
              <Button
                variant="contained"
                color="primary"
                ref={addAnchor}
                disabled={getCreateDisabled()}
                className={classes.button}
                onClick={() => {
                  if (documentType === DocumentTemplateType.PunchList) {
                    setCreatePunchListDialogOpen(true);
                  } else {
                    setAddDialogOpen(true);
                  }
                }}
              >
                CREATE
              </Button>
            </Hidden>
          </span>
        ),

        <>
          {documentType === DocumentTemplateType.PunchList && (
            <>
              {!isEditingCost ? (
                <Button
                  ref={editCostAnchor}
                  variant="contained"
                  startIcon={<AttachMoney />}
                  color="primary"
                  disabled={permission < SecurityPermissionLevel.NUMBER_3}
                  onClick={() => setIsEditingCost(true)}
                  className={classes.button}
                >
                  Edit Cost
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={() => setIsEditingCost(false)}
                    className={classes.button}
                    style={{ backgroundColor: '#ED3F26', color: '#FFF', height: 32 }}
                  >
                    Cancel
                  </Button>
                  <SubmitButton
                    variant="contained"
                    startIcon={<Save />}
                    onClick={submitCosts}
                    className={classes.button}
                  >
                    Save Changes
                  </SubmitButton>
                </>
              )}
              {!isEditingCost && (
                <>
                  <Button
                    ref={punchListStatusAnchor}
                    disabled={
                      selected.length === 0 ||
                      isEditingCost ||
                      permission < SecurityPermissionLevel.NUMBER_2 ||
                      hasSelectedWithdrawnDocuments ||
                      hasSelectedDeletedDocuments
                    }
                    variant="contained"
                    color="primary"
                    startIcon={<Edit />}
                    className={classes.button}
                    onClick={() => setEditPunchListStatusDialogOpen(true)}
                  >
                    EDIT STATUS
                  </Button>

                  <Button
                    ref={responsiblePartyAnchor}
                    disabled={
                      selected.length === 0 ||
                      isEditingCost ||
                      permission < SecurityPermissionLevel.NUMBER_2 ||
                      hasSelectedWithdrawnDocuments ||
                      hasSelectedDeletedDocuments
                    }
                    variant="contained"
                    startIcon={<Person />}
                    color="primary"
                    className={classes.button}
                    onClick={() => setEditResponsiblePartyDialogOpen(true)}
                  >
                    Set Responsible Subcontractor
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={
                      isPublishingPunchListItems ? (
                        <CircularLoader size={18} loaderStyle={{ color: '#A3A3A3' }} />
                      ) : (
                        <Publish />
                      )
                    }
                    color="primary"
                    className={classes.button}
                    disabled={
                      selected.length === 0 ||
                      selectedDocuments.some((d) => !d.isDraft) ||
                      isPublishingPunchListItems ||
                      hasSelectedWithdrawnDocuments ||
                      hasSelectedDeletedDocuments
                    }
                    onClick={handlePublishPunchListItems}
                  >
                    Publish
                  </Button>

                  <Button
                    ref={sendALinkAnchor}
                    disabled={selected.length !== 1 || isEditingCost || hasSelectedDeletedDocuments}
                    variant="contained"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faCopy}
                        style={{ width: 18, height: 18, color: '#FFF' }}
                      />
                    }
                    color="primary"
                    className={classes.button}
                    onClick={() => setSendALinkDialogOpen(true)}
                  >
                    Send A Link
                  </Button>
                </>
              )}
            </>
          )}
        </>,
      );
    } else if (
      (documentType === DocumentTemplateType.Drawings ||
        documentType === DocumentTemplateType.Specifications) &&
      deletePermission
    ) {
      buttons.push(
        !isDeleting ? (
          <SmallButton
            variant="contained"
            color="primary"
            disabled={selected.length === 0 || hasSelectedDeletedDocuments}
            className={classes.deleteButton}
            onClick={handleDeleteDocuments}
          >
            <Delete style={{ width: 20, height: 20, paddingRight: 2 }} />
            Delete
          </SmallButton>
        ) : (
          <CircularLoader size={30} style={{ marginLeft: 32, marginRight: 32 }} />
        ),
      );
    }

    if (
      documentType === DocumentTemplateType.Drawings ||
      documentType === DocumentTemplateType.Specifications
    ) {
      buttons.push(
        <>
          {!isDownloading ? (
            <Button
              color="primary"
              variant="contained"
              disabled={filteredRows.length === 0 || hasSelectedDeletedDocuments}
              onClick={handleDownloadFiles}
            >
              Download{' '}
              {selected.length > 0 && selected.length !== filteredRows.length ? 'Selected' : 'All'}
            </Button>
          ) : (
            <CircularLoader size={30} style={{ marginLeft: 32, marginRight: 40 }} />
          )}
        </>,
      );
    }

    if (documentType === DocumentTemplateType.Drawings) {
      buttons.push(
        <Button
          variant="contained"
          color="primary"
          ref={editDrawingsPackageAnchor}
          disabled={selected.length === 0 || !addEditPermission || hasSelectedDeletedDocuments}
          onClick={() => setEditDrawingsPackageDialogOpen(true)}
        >
          Edit Package
        </Button>,
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            ref={associateAnchor}
            disabled={associableDocuments.length === 0 || !addEditPermission}
            style={
              isDesktop
                ? {
                    height: '32px',
                    padding: '6px 10px',
                  }
                : {}
            }
            onClick={() => setAssociateDialogOpen(true)}
          >
            <EditOutlined style={{ width: 20, height: 20, paddingRight: 2 }} />
            Associate Users with Drawings
          </Button>
          <Tooltip
            arrow
            placement="top"
            title="Use this button to associate design team members with their respective drawings."
          >
            <Info style={{ marginLeft: 3, marginRight: 2 }} />
          </Tooltip>
        </div>,
      );
    }

    if (documentType === DocumentTemplateType.Specifications && (!isMobile || !visible)) {
      buttons.push(
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            ref={associateAnchor}
            disabled={associableDocuments.length === 0 || !addEditPermission}
            style={{
              height: '32px',
              padding: '6px 10px',
            }}
            onClick={() => setAssociateDialogOpen(true)}
          >
            <EditOutlined style={{ width: 20, height: 20, paddingRight: 2 }} />
            Associate Users with Specifications
          </Button>
          <Tooltip
            arrow
            placement="top"
            title="Use this button to associate design team members with their respective specifications."
          >
            <Info style={{ marginLeft: 3, marginRight: 2 }} />
          </Tooltip>
        </div>,
      );
    }

    if (user.isSiteAdmin) {
      buttons.push(
        <Button
          variant="contained"
          color="primary"
          disabled={selectedDocuments.length === 0 || selectedDocuments.some((d) => !d.isHidden)}
          onClick={restoreSelectedDocuments}
        >
          Restore
        </Button>,
      );
    }

    if (
      (documentType === DocumentTemplateType.Task ||
        documentType === DocumentTemplateType.CaTask) &&
      setTaskScope
    ) {
      ([
        {
          scope: 'me',
          label: 'Show Tasks for Me',
          minLevel: TaskManagerPermission.NUMBER_1,
        },
        {
          scope: 'company',
          label: 'Show Tasks for My Company',
          minLevel: TaskManagerPermission.NUMBER_2,
        },
        {
          scope: 'everyone',
          label: 'Show Tasks for Everyone',
          minLevel: TaskManagerPermission.NUMBER_3,
        },
      ] as { scope: 'me' | 'company' | 'everyone'; label: string; minLevel: number }[]).forEach(
        ({ scope, label, minLevel }) => {
          const allowed = taskManagerPermission != null && taskManagerPermission >= minLevel;
          const button = (
            <Button
              color="primary"
              variant={taskScope === scope ? 'contained' : 'outlined'}
              disabled={!allowed}
              onClick={() => setTaskScope(scope)}
              style={{ whiteSpace: 'nowrap' }}
            >
              {label}
            </Button>
          );
          buttons.push(
            allowed ? (
              <span key={scope}>{button}</span>
            ) : (
              <Tooltip key={scope} title="Permission disabled" placement="top" arrow>
                <span>{button}</span>
              </Tooltip>
            ),
          );
        },
      );
    }

    return buttons;
  };

  const restoreSelectedDocuments = async () => {
    try {
      await restoreDocumentsByIds(selected);
      dispatch(reloadDocuments());
    } catch {
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: 'Something went wrong while trying to restore this document',
          severity: 'error',
        }),
      );
    }
  };

  // export all rows to csv if none are selected, otherwise only export the ones that are selected
  const getCsvData = async () => {
    setCsvLoading(true);
    if (!documentType) return [];

    let targetRows: any[];
    if (documentType === DocumentTemplateType.PunchList) {
      const punchListRows = filteredRows as PunchListLocationRow[];
      const allItemIds = punchListRows.flatMap((row) => row.children.map((item) => item.id));
      const exportEverything =
        selected.length === 0 || allItemIds.every((id) => selected.includes(id));

      targetRows = exportEverything
        ? punchListRows
        : punchListRows
            .map((row) => ({
              ...row,
              children: row.children.filter((item) => selected.includes(item.id)),
            }))
            .filter((row) => row.children.length > 0);
    } else {
      targetRows =
        selected.length === 0
          ? filteredRows
          : filteredRows.filter((row) => selected.includes(row.id));
    }

    const rows = await getCsvRowsFromDocumentIndexRows(
      targetRows,
      documentType,
      packages,
      buildings,
      floors,
      locations,
      actionTakenLabels,
    );

    setCsvLoading(false);
    return rows;
  };

  const getCsvFilename = () => {
    return `${selectedProject!.name} - ${getUserFriendlyDocumentTemplateNamePlural(
      documentType!,
    )}.csv`;
  };

  const handlePrint = useReactToPrint({
    content: () => componentReference.current,
    onBeforeGetContent: async () => {
      emitter.emit(Events.ExpandRows);
    },
    onAfterPrint: async () => {
      emitter.emit(Events.CloseRows);
    },
  });

  const showDrawer = () => {
    if (!documentType) return false;
    if (
      documentType === DocumentTemplateType.Specifications ||
      documentType === DocumentTemplateType.Submittals ||
      documentType === DocumentTemplateType.CloseoutSubmittals
    )
      return true;
    return false;
  };

  const showFilter = () => {
    if (!documentType) return false;
    if (user.isSiteAdmin) return true;
    if (docTypesNoFilter.includes(documentType)) return false;
    return !(
      !selectedProject.isProcoreIntegrationEnabled && docTypesProcoreFilter.includes(documentType)
    );
  };

  const handleSyncProcoreDocument = async () => {
    setIsSyncingWithProcore(true);
    switch (documentType) {
      case DocumentTemplateType.Submittals:
        beginSyncSubmittalsAndPackages(selectedProject.id);
        break;
      case DocumentTemplateType.RequestsForInformation:
        beginSyncRfis(selectedProject.id);
        break;
      case DocumentTemplateType.PunchList:
        beginSyncPunchLists(selectedProject.id);
        break;
    }
    setIsSyncingWithProcore(false);
    dispatch(reloadDocuments());
  };

  const handleAssociateUsers = async (userIds: string[], documentIds: string[]) => {
    await modifyDocumentsByBatch(documentIds, { followers: { addUserIds: userIds } });
    dispatch(reloadDocuments());
  };

  const handleSubmitPackageReview = async (review: SubmittalInfo[]) => {
    if (!selectedDocument) return;
    await completeReviewForPackageByDocumentId(
      selectedDocument.id,
      review.map(({ document, actionTaken, comment }) => ({
        documentId: document.id,
        actionTaken,
        comment,
      })),
    );
    dispatch(reloadDocuments());
  };

  useEffect(() => {
    if (documentType === DocumentTemplateType.PunchList) {
      if (sessionStorage.getItem('viewByFloorplanState')) setFloorplanDialogOpen(true);
    }
  }, []);

  // Kept out of the action buttons on the left: Export sits with Print and CSV at the top right,
  // Expand All at the start of the Filter and Search row.
  const showPunchListRowControls =
    documentType === DocumentTemplateType.PunchList && !isEditingCost;

  return (
    <Toolbar className={clsx(classes.toolbar)}>
      <div className={classes.root}>
        {/* On a line of its own, above the buttons. */}
        {(documentType === DocumentTemplateType.Submittals ||
          documentType === DocumentTemplateType.CloseoutSubmittals) && (
          <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
            {getTitle()}
          </Typography>
        )}
        {/* Actions on the left; printing and exporting at the top right, above Search. */}
        <div className={clsx(classes.row, classes.actionRow)}>
          <div className={classes.actionButtons}>{getToolbarButton()}</div>
          {selectedProject.isProcoreIntegrationEnabled &&
            user.isSiteAdmin &&
            (documentType === DocumentTemplateType.RequestsForInformation ||
              documentType === DocumentTemplateType.Submittals) && (
              <ProcoreButton
                variant="contained"
                color="primary"
                onClick={handleSyncProcoreDocument}
                style={{ margin: isDesktop ? 0 : '5px 0px' }}
              >
                {isSyncingWithProcore ? 'Syncing...' : 'Sync with Procore'}
              </ProcoreButton>
            )}
          {showDrawer() && !isDesktop && (
            <Button
              color="primary"
              variant="contained"
              style={{ margin: isDesktop ? 0 : '5px 0px' }}
              onClick={() => {
                if (setIndexDrawerOpen !== undefined) setIndexDrawerOpen(true);
              }}
            >
              {getDrawerText()}
            </Button>
          )}
          <div className={clsx(classes.rowEnd, classes.actionRowEnd)}>
            <Tooltip title="Print" arrow placement="left">
              <IconButton size="small">
                <Print style={{ display: 'inline-flex' }} onClick={handlePrint} />
              </IconButton>
            </Tooltip>
            <CsvDownloader
              filename={getCsvFilename()}
              datas={getCsvData}
              columns={
                documentType === DocumentTemplateType.PunchList ? punchListCsvHeaders : undefined
              }
            >
              {csvLoading ? (
                <CircularLoader size={27} />
              ) : (
                <Tooltip title="Export to CSV" arrow placement="left">
                  <IconButton size="small">
                    <CSVIcon style={{ display: 'inline-flex' }} />
                  </IconButton>
                </Tooltip>
              )}
            </CsvDownloader>
            {showPunchListRowControls && (
              // Always live: what to export is chosen in the dialog, not by the rows ticked here.
              <Button
                variant="contained"
                startIcon={<Share />}
                color="primary"
                className={classes.button}
                onClick={() => setPunchListExportSelectionOpen(true)}
              >
                Export
              </Button>
            )}
          </div>
        </div>
        {/* Filter and Search together at the right, whatever else the row holds. */}
        <div className={clsx(classes.row, classes.filterRow)}>
          {showPunchListRowControls && (
            <Button
              variant="contained"
              color="primary"
              className={clsx(classes.button, classes.expandAll)}
              startIcon={allLocationsExpanded ? <ChevronRight /> : <ExpandMore />}
              onClick={() =>
                emitter.emit(allLocationsExpanded ? Events.CloseRows : Events.ExpandRows)
              }
            >
              {allLocationsExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          )}
          <div className={clsx(classes.rowEnd, classes.searchContainer)}>
            {showFilter() ? (
              <Button
                color="primary"
                variant="contained"
                ref={filterAnchor}
                className={classes.filterButton}
                onClick={() => setFilterDialogOpen(true)}
              >
                {getFilterText()}
              </Button>
            ) : null}
            {/* Search Bar */}
            <FormControl className={classes.formControl}>
              <TextField
                placeholder="Search"
                variant="outlined"
                size="small"
                value={inputSearch}
                onChange={(e) => handleSearchChange(e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        fontSize="small"
                        classes={{
                          root: classes.icon,
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </div>
        </div>
      </div>
      <DocumentLogFilterDialog
        dialogOpen={filterDialogOpen}
        handleClose={() => setFilterDialogOpen(false)}
        anchor={filterAnchor.current}
        selectedProject={selectedProject}
        filters={filters}
        updateFilters={setFilters}
        updateNumFilters={setNumFilters}
        updateRows={updateRows}
      />
      <EditDocumentsDialog
        dialogOpen={editDialogOpen}
        handleClose={() => {
          setEditDialogOpen(false);
          if (setUploadWorkflowOpen) setUploadWorkflowOpen(false);
        }}
        anchor={editAnchor.current || packageAnchor.current}
        selected={selected}
        addToCloseout={addToCloseout}
        removeFromCloseout={removeFromCloseout}
        selectedAreProcoreSynced={selectedAreProcoreSynced}
        selectedDocument={selectedDocument}
        canChangeAnticipatedSubmission={canChangeAnticipatedSubmission}
      />
      <AssociateUsersWithDocumentsDialog
        open={associateDialogOpen}
        handleClose={() => setAssociateDialogOpen(false)}
        type={
          documentType === DocumentTemplateType.Drawings
            ? AssociationDocumentType.Drawings
            : AssociationDocumentType.Specifications
        }
        documents={associableDocuments}
        onAssociate={handleAssociateUsers}
      />
      <AddPackageDialog
        dialogOpen={addPackageDialogOpen}
        handleClose={() => setAddPackageDialogOpen(false)}
        anchor={packageAnchor.current}
        selected={selected}
      />
      <AddPackageDialog
        dialogOpen={addPlaceholderPackageDialogOpen}
        handleClose={() => setAddPlaceholderPackageDialogOpen(false)}
        anchor={placeholderPackageAnchor.current}
        selected={selected}
        isPlaceholder
      />
      {/* The dashboard's flow, step one: which submittals the package gathers. */}
      {selectSubmittalsOpen ? (
        <SelectSubmittalsDialog
          open
          projectId={selectedProject.id}
          documentType={documentType!}
          isPlaceholder={packagePickerIsPlaceholder}
          handleClose={() => setSelectSubmittalsOpen(false)}
          onCreatePackage={(selections) => {
            setSelectSubmittalsOpen(false);
            setPackageSelections(selections);
          }}
        />
      ) : null}
      {/* Step two: naming it, and -- unless it is a placeholder -- the file it carries. */}
      {packageSelections ? (
        <AddPackageDialog
          dialogOpen
          handleClose={() => setPackageSelections(undefined)}
          anchor={placeholderPackageAnchor.current}
          selected={
            packageSelections.map((selection) => selection.documentId).filter(Boolean) as string[]
          }
          submittalsToCreate={
            packageSelections.map((selection) => selection.create).filter(Boolean) as NonNullable<
              SubmittalPackageSelection['create']
            >[]
          }
          isPlaceholder={packagePickerIsPlaceholder}
          selectedSummaries={packageSelections.map((selection) => ({
            id: selection.key,
            label: selection.label,
          }))}
        />
      ) : null}
      <AddDocumentDialog
        dialogOpen={addDialogOpen}
        handleClose={() => setAddDialogOpen(false)}
        anchor={addAnchor.current}
      />
      <EditResponsiblePartyDialog
        dialogOpen={editResponsiblePartyDialogOpen}
        onClose={() => setEditResponsiblePartyDialogOpen(false)}
        anchor={responsiblePartyAnchor.current}
        selected={selected}
      />
      <EditPunchListStatusDialog
        dialogOpen={editPunchListStatusDialogOpen}
        handleClose={() => setEditPunchListStatusDialogOpen(false)}
        anchor={punchListStatusAnchor.current}
        selected={selected}
      />
      <ViewByFloorplanDialog
        open={floorplanDialogOpen}
        onClose={() => setFloorplanDialogOpen(false)}
      />
      <EmailUsersDialog
        dialogOpen={sendALinkDialogOpen}
        closeDialog={() => setSendALinkDialogOpen(false)}
        onSubmit={handleSendEmails}
        getLink={handleGetLink}
        defaultSubject={`New Link to Document in ${selectedProject.name}`}
      />
      <PunchListExportSelectionDialog
        open={punchListExportSelectionOpen}
        closeDialog={() => setPunchListExportSelectionOpen(false)}
        projectId={selectedProject.id}
        // Ticking a location row in the table ticks its items, so this is already a list of items.
        initialSelectedItemIds={selected}
        onSubmit={(documentIds) => {
          setPunchListExportSelectionOpen(false);
          setPunchListExportDocumentIds(documentIds);
        }}
      />
      <PunchListExportDialog
        open={!!punchListExportDocumentIds}
        onClose={() => setPunchListExportDocumentIds(undefined)}
        projectId={selectedProject.id}
        projectName={selectedProject.name ?? 'Project'}
        selection={{ locationIds: [], documentIds: punchListExportDocumentIds ?? [] }}
      />
      {selectedDocument ? (
        <>
          {!isAsBuiltPackage ? (
            <EditAsBuiltStatusDialog
              anchor={editAnchor.current}
              dialogOpen={editAsBuiltStatusDialogOpen}
              handleClose={() => setEditAsBuiltStatusDialogOpen(false)}
              selectedDocument={selectedDocument}
            />
          ) : (
            <RefusePackageDialog
              isOpen={editAsBuiltStatusDialogOpen}
              handleClose={() => setEditAsBuiltStatusDialogOpen(false)}
              packageDocuments={selectedDocument?.submittalChildDocuments || []}
              submit={handleSubmitPackageReview}
              selectedDocument={selectedDocument}
            />
          )}
        </>
      ) : null}
      <CreatePunchListDialog
        open={createPunchListDialogOpen}
        handleClose={() => setCreatePunchListDialogOpen(false)}
        anchor={addAnchor.current}
      />
      <EditDrawingsPackageDialog
        dialogOpen={editDrawingsPackageDialogOpen}
        handleClose={() => setEditDrawingsPackageDialogOpen(false)}
        anchor={editDrawingsPackageAnchor.current}
        selectedDocuments={selectedDocuments}
      />
    </Toolbar>
  );
}
